'use client';

import { Box } from '@mui/material';

interface EmbeddedMapProps {
  mapUrl: string;
  width?: string | number;
  height?: string | number;
}

export default function EmbeddedMap({
  mapUrl,
  width = '100%',
  height = '286px',
}: EmbeddedMapProps) {
  // Convert various Google Maps URLs to embeddable format
  const getEmbedUrl = (url: string): string => {
    try {
      // If it's already a Google Maps embed URL, use it directly
      if (url.includes('google.com/maps/embed')) {
        return url;
      }

      // Handle maps.app.goo.gl URLs
      if (url.includes('maps.app.goo.gl')) {
        // For short URLs, we need to convert them to embed format
        // Since we can't extract coordinates from short IDs, we'll use a fallback
        return `https://maps.google.com/maps?q=Vietnam&output=embed&t=m&z=15`;
      }

      // Handle standard Google Maps URLs
      if (url.includes('maps.google.com/maps')) {
        const urlObj = new URL(url);
        const query = urlObj.searchParams.get('q');
        if (query) {
          return `https://maps.google.com/maps?q=${encodeURIComponent(query)}&output=embed&t=m&z=15`;
        }
      }

      // Handle Google Maps place URLs
      if (url.includes('maps.google.com/maps/place')) {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname;
        const placeName = pathname.replace('/maps/place/', '').split('/')[0];
        if (placeName) {
          return `https://maps.google.com/maps?q=${encodeURIComponent(placeName)}&output=embed&t=m&z=15`;
        }
      }

      // Handle Google Maps search URLs
      if (url.includes('maps.google.com/maps/search')) {
        const urlObj = new URL(url);
        const query = urlObj.searchParams.get('q');
        if (query) {
          return `https://maps.google.com/maps?q=${encodeURIComponent(query)}&output=embed&t=m&z=15`;
        }
      }

      // For any other URL, try to extract location info
      const urlObj = new URL(url);
      const searchParams = urlObj.searchParams;
      const pathname = urlObj.pathname;

      const location =
        searchParams.get('q') ||
        searchParams.get('address') ||
        searchParams.get('location') ||
        pathname.split('/').pop();

      if (location) {
        return `https://maps.google.com/maps?q=${encodeURIComponent(location)}&output=embed&t=m&z=15`;
      }
    } catch (error) {
      console.warn('Error parsing map URL:', error);
    }

    // Fallback: return a default location
    return `https://maps.google.com/maps?q=Vietnam&output=embed&t=m&z=15`;
  };

  const embedUrl = getEmbedUrl(mapUrl);

  return (
    <Box
      sx={{
        width,
        height,
        borderRadius: '8px',
        overflow: 'hidden',
        border: '1px solid #e0e0e0',
      }}
    >
      <iframe
        src={embedUrl}
        width='100%'
        height='100%'
        style={{ border: 0 }}
        allowFullScreen
        loading='lazy'
        referrerPolicy='no-referrer-when-downgrade'
        title='Google Maps'
      />
    </Box>
  );
}
